import { useCallback, type CSSProperties } from 'react';
import { Activity, AlertTriangle, Gauge, Zap } from 'lucide-react';
// GenericDrawer is a JS file with many props that tsc cannot fully infer; cast to permissive FC
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import _GenericDrawer from '@/components/ui/GenericDrawer';
const GenericDrawer = _GenericDrawer as React.FC<any>;
import { supabase } from '@/lib/supabase';
import FieldsetCard from '@/components/ui/FieldsetCard';
import LoadingState from '@/components/ui/LoadingState';
import { getKeyMetrics, listRecentRequests } from './api-keys.service';
import type { ApiKey, KeyMetrics, KeyRequestLogEntry } from './types';

interface KeyDashboardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  recordId?: string | null;
  drawerWidth?: string;
}

interface DashboardData extends Record<string, unknown> {
  key: ApiKey;
  metrics: KeyMetrics;
  recent: KeyRequestLogEntry[];
}

const METRICS_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '12px',
  padding: '12px',
};

const METRIC_CARD_STYLE: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '14px 16px',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const METRIC_LABEL_STYLE: CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const METRIC_VALUE_STYLE: CSSProperties = {
  fontSize: '22px',
  fontWeight: 700,
  color: '#111827',
  lineHeight: 1.1,
};

const METRIC_SUFFIX_STYLE: CSSProperties = {
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b7280',
  marginLeft: '4px',
};

const TABLE_STYLE: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '12px',
};

const TH_STYLE: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '10px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const TD_STYLE: CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
  color: '#374151',
  whiteSpace: 'nowrap',
};

const EMPTY_STYLE: CSSProperties = {
  padding: '32px 16px',
  textAlign: 'center',
  fontSize: '13px',
  color: '#6b7280',
};

function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('pt-BR');
}

function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  return Math.round(ms).toString();
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function statusColor(status: number): { bg: string; fg: string } {
  if (status >= 500) return { bg: '#fee2e2', fg: '#991b1b' };
  if (status >= 400) return { bg: '#fef3c7', fg: '#92400e' };
  if (status >= 300) return { bg: '#e0e7ff', fg: '#3730a3' };
  return { bg: '#dcfce7', fg: '#166534' };
}

export default function KeyDashboardDrawer({
  isOpen,
  onClose,
  recordId,
  drawerWidth = '50%',
}: KeyDashboardDrawerProps) {
  const handleLoadRecord = useCallback(async (id: string): Promise<DashboardData> => {
    const [keyRes, metrics, recent] = await Promise.all([
      supabase
        .from('api_key')
        .select('id, name, prefix, scopes, ip_allowlist, last_used_at, expires_at, revoked_at, created_at')
        .eq('id', id)
        .single(),
      getKeyMetrics(id, 24),
      listRecentRequests(id, 20),
    ]);
    if (keyRes.error) throw keyRes.error;
    return { key: keyRes.data as ApiKey, metrics, recent };
  }, []);

  const renderContent = ({ formData }: { formData: Partial<DashboardData> | null }) => {
    // GenericDrawer paints renderContent with an empty {} before onLoadRecord
    // resolves; render a loading state until the three async results arrive.
    if (!formData?.key || !formData?.metrics || !formData?.recent) {
      return <LoadingState message="Carregando atividade da chave..." />;
    }
    const { key, metrics, recent } = formData as DashboardData;

    const errorRate =
      metrics.requests > 0 ? Math.round((metrics.errors / metrics.requests) * 100) : 0;

    return (
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <FieldsetCard title={`${key.name} · ${key.prefix}`} shadow="md" contentStyle={{ padding: 0 }}>
          <div style={METRICS_GRID_STYLE}>
            <div style={METRIC_CARD_STYLE}>
              <div style={METRIC_LABEL_STYLE}>
                <Activity size={12} color="#154680" />
                Requisições · 24h
              </div>
              <div style={METRIC_VALUE_STYLE}>{formatNumber(metrics.requests)}</div>
            </div>

            <div style={METRIC_CARD_STYLE}>
              <div style={METRIC_LABEL_STYLE}>
                <AlertTriangle size={12} color={errorRate > 5 ? '#dc2626' : '#6b7280'} />
                Erros (4xx + 5xx)
              </div>
              <div style={METRIC_VALUE_STYLE}>
                {formatNumber(metrics.errors)}
                {metrics.requests > 0 && (
                  <span style={METRIC_SUFFIX_STYLE}>· {errorRate}%</span>
                )}
              </div>
            </div>

            <div style={METRIC_CARD_STYLE}>
              <div style={METRIC_LABEL_STYLE}>
                <Gauge size={12} color="#154680" />
                Latência p50
              </div>
              <div style={METRIC_VALUE_STYLE}>
                {formatLatency(metrics.p50_ms)}
                <span style={METRIC_SUFFIX_STYLE}>ms</span>
              </div>
            </div>

            <div style={METRIC_CARD_STYLE}>
              <div style={METRIC_LABEL_STYLE}>
                <Zap size={12} color="#154680" />
                Latência p95
              </div>
              <div style={METRIC_VALUE_STYLE}>
                {formatLatency(metrics.p95_ms)}
                <span style={METRIC_SUFFIX_STYLE}>ms</span>
              </div>
            </div>
          </div>
        </FieldsetCard>

        <FieldsetCard
          title="Últimas requisições"
          count={recent.length}
          shadow="md"
          contentStyle={{ padding: 0 }}
        >
          {recent.length === 0 ? (
            <div style={EMPTY_STYLE}>Nenhuma requisição registrada para esta chave ainda.</div>
          ) : (
            <table style={TABLE_STYLE}>
              <thead>
                <tr>
                  <th style={TH_STYLE}>Hora</th>
                  <th style={TH_STYLE}>Método</th>
                  <th style={TH_STYLE}>Rota</th>
                  <th style={TH_STYLE}>Status</th>
                  <th style={{ ...TH_STYLE, textAlign: 'right' }}>Latência</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((req, idx) => {
                  const c = statusColor(req.status);
                  return (
                    <tr
                      key={req.id}
                      style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}
                    >
                      <td style={{ ...TD_STYLE, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                        {formatTime(req.created_at)}
                      </td>
                      <td style={{ ...TD_STYLE, fontWeight: 600, color: '#374151' }}>
                        {req.method}
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily: 'monospace', fontSize: '11px' }}>
                        {req.route}
                      </td>
                      <td style={TD_STYLE}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: c.bg,
                            color: c.fg,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {req.status}
                        </span>
                      </td>
                      <td
                        style={{
                          ...TD_STYLE,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: req.latency_ms > 1000 ? '#92400e' : '#374151',
                        }}
                      >
                        {req.latency_ms} ms
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </FieldsetCard>
      </div>
    );
  };

  return (
    <GenericDrawer
      isOpen={isOpen}
      onClose={onClose}
      recordId={recordId || ''}
      title="ATIVIDADE DA CHAVE"
      drawerWidth={drawerWidth}
      onLoadRecord={handleLoadRecord}
      canEdit={false}
      canDelete={false}
      renderContent={renderContent}
    />
  );
}
